import { PatientTherapyEditDrawer } from '@app/icare/entities/patient/edits/PatientTherapyEditDrawer'
import { patientFindingsQueryOptions } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { patientTherapyQueries } from '@app/icare/entities/patient/queries/patientTherapyQueries'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import type { PatientTherapyRelation } from '@app/icare/entities/patient/schema/PatientTherapy'
import { TherapyTypeEnum } from '@app/icare/entities/therapy/schema/TherapyType'
import { RelationTabs } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationTabs'
import { useRelationCommitNavigation } from '@app/icare/routes/_app/patienten/$id/_edit/-components/useRelationCommitNavigation'
import { isActivePatientRelation } from '@app/icare/shared/api/form.type'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_therapy/therapien')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(patientTherapyQueries.forTabs(params.id)),
      queryClient.ensureQueryData(patientFindingsQueryOptions(params.id)),
    ]),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: therapies = [] } = useSuspenseQuery(patientTherapyQueries.forTabs(id))
  const { data: findings = [] } = useQuery(patientFindingsQueryOptions(id))
  const { data: totalCount } = useQuery(patientTherapyQueries.totalCount(id))

  const handleAfterCommit = useRelationCommitNavigation<PatientTherapyRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/therapien/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })

  return (
    <RelationTabs
      relations={therapies}
      relationTotalCount={totalCount}
      basePath={`/patienten/${id}/therapien`}
      findings={findings}
      findingsGroup="therapies"
      getLabel={item => TherapyTypeEnum.$.getLabel(item.type)}
      onAdd={params =>
        void PatientTherapyEditDrawer.show({
          patientId: id,
          onAfterCommit: handleAfterCommit,
          ...params,
        })
      }
      addLabel={t`Therapie hinzufügen`}
    />
  )
}
